<script>
  import gql from 'graphql-tag'

  // Who can be emailed. The label is what the merchant calls the role; the suffix
  // is what the setting key calls it. They do not line up — "Tech" is the *_owner
  // key (the user the email is about) and "Owner" is the *_master key (the master
  // account) — so the mapping is pinned here once instead of at 20 call sites.
  const ROLES = [
    { suffix: 'Owner', label: 'Tech' },
    { suffix: 'Supervisor', label: 'Supervisors' },
    { suffix: 'Accountant', label: 'Accountants' },
    { suffix: 'Master', label: 'Owner' }
  ]

  // What we send, in the order a merchant is likely to care about it. `locked`
  // lists roles that cannot be switched off for that kind of email.
  const GROUPS = [
    {
      key: 'log',
      title: 'Transaction receipts',
      description: 'A receipt for every completed payment.'
    },
    {
      key: 'votesFeedback',
      title: 'Votes and feedback',
      description: 'Thumbs-down ratings, low scores, and feedback.'
    },
    {
      key: 'compactLog',
      title: 'Batch emails',
      description: 'Payout confirmations and daily sales summaries.'
    },
    {
      key: 'info',
      title: 'Recurring, voids and refunds',
      description: 'Recurring charges, voids, refunds and adjustments.',
      locked: ['Master']
    },
    {
      key: 'critical',
      title: 'Critical alerts',
      description: 'Disputes, bad checks, and failed recurring charges.',
      locked: ['Master']
    }
  ]

  // One source of truth for the 20 field names, so the query, the mutation and
  // the params we post can never drift apart.
  const FIELD_NAMES = GROUPS.reduce(function (acc, group) {
    return acc.concat(ROLES.map(function (role) { return group.key + role.suffix }))
  }, [])
  const FIELDS = FIELD_NAMES.join(' ')

  export default {
    props: ['gid'],
    apollo: {
      settings: {
        fetchPolicy: "no-cache",
        query: gql`query($gid: ID) {
          merchant(gid: $gid) { merchantUser {settings {notifications {${FIELDS}}}}}
        }`,
        variables() {
          return {
            gid: this.gid
          }
        },
        update: data => data.merchant.merchantUser.settings.notifications
      }
    },
    data() {
      return {
        settings: {},
        roles: ROLES,
        groups: GROUPS
      }
    },
    methods: {
      updateSettings() {
        this.$apollo.mutate({
          // Query
          mutation: gql`mutation ($params: NotificationsAttributes!, $gid: ID) {
            updateNotifications(input: {notifications: $params, userId: $gid}) {
              notifications { ${FIELDS} }
              errors
            }
          }`,
          // Parameters
          variables: {
            params: this.params,
            gid: this.gid
          },
        }).then(result => {
          this.settings = result.data.updateNotifications.notifications
          this.notifySuccess("Notifications settings successfully updated")
        }, error => {
          this.$apollo.queries.settings.refetch()
          this.gqlErrorHandler(error)
        })
      },
      field(group, role) {
        return group.key + role.suffix
      },
      locked(group, role) {
        return (group.locked || []).indexOf(role.suffix) !== -1
      }
    },
    computed: {
      loading() {
        return this.$apollo.queries.settings.loading
      },
      params() {
        return FIELD_NAMES.reduce((acc, name) => {
          acc[name] = this.settings[name]
          return acc
        }, {})
      }
    }
  }
</script>
<template>
  <div class="notif-settings">
    <p class="notif-lede">Choose who on your team receives each kind of email.</p>

    <div v-if="loading" class="notif-loading">Loading settings...</div>

    <div v-else class="surface-card notif-matrix">
      <div class="notif-grid">
        <div class="notif-grid__head">Notification</div>
        <div class="notif-grid__head" v-for="role in roles" :key="role.suffix">{{ role.label }}</div>

        <div class="notif-row" v-for="group in groups" :key="group.key">
          <div class="notif-row__info">
            <div class="notif-row__title">{{ group.title }}</div>
            <div class="notif-row__desc">{{ group.description }}</div>
          </div>

          <div
            v-for="role in roles"
            :key="role.suffix"
            class="notif-row__cell"
            :class="{ 'notif-row__cell--locked': locked(group, role) }"
            :data-role="role.label">
            <div class="switch">
              <label :title="locked(group, role) ? 'Always on' : null">
                <input
                  type="checkbox"
                  :disabled="locked(group, role)"
                  :aria-label="group.title + ' for ' + role.label"
                  v-model="settings[field(group, role)]"
                  @change="updateSettings">
                <span class="lever"></span>
              </label>
            </div>
            <i v-if="locked(group, role)" class="fas fa-lock notif-lock" title="Always on"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
